import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, ReplaySubject } from 'rxjs';
import { doGetPromise, doDeletePromise } from '../../../core/config/core.httpclient-utils';

@Injectable()
export class WikiService {
    attachmentCount$ = new Subject<number>();
    /** The wiki page the container has currently loaded — lets sibling views (e.g. the page
     *  header in the projectheader slot) show page details without re-fetching. ReplaySubject so a
     *  late subscriber still receives the last-loaded page. */
    wikiPage$ = new ReplaySubject<any>(1);

    constructor(private http: HttpClient) {
    }

    fetch(params) {
        return doGetPromise(this.http, `/api/wiki`, params);
    }

    page(id, params) {
        return doGetPromise(this.http, `/api/wiki/${id}/content`, params);
    }

    versions(id, params: any = {}) {
        return doGetPromise(this.http, `/api/wiki/${id}/versions`, params);
    }

    referrers(id, params: any = {}) {
        return doGetPromise(this.http, `/api/wiki/${id}/referrers`, params);
    }

    diff(id, params: any = {}) {
        return doGetPromise(this.http, `/api/wiki/${id}/diff`, params);
    }

    info(id, params: any = {}) {
        return doGetPromise(this.http, `/api/wiki/${id}`, params);
    }

    update(id, data: any = {}) {
        return this.http.post(`/api/wiki/${id}`, data)
            .toPromise()
    }

    postOptions(id: any, data: any = {}) {
        return this.http.post(`/api/wiki/${id}/options`, data)
            .toPromise()
    }

    create(unixName: string, data: any = {}) {
        return this.http.post(`/api/wiki/${unixName}/create`, data)
            .toPromise()
    }

    /** The forum thread holding a project wiki page's comments; rejects with a 404 while the page has none. */
    getCommentThread(id: any) {
        return doGetPromise(this.http, `/api/wiki/${id}/commentthread`);
    }

    /** Get or create the comment thread for a project wiki page. */
    createCommentThread(id: any) {
        return this.http.post(`/api/wiki/${id}/commentthread`, {})
            .toPromise()
    }

    /**
     * Every comment in a page's thread: comment threads are small, so one page of results holds them all.
     * Resolves with the message list, unwrapped from the paged response.
     */
    getComments(threadId: number) {
        return doGetPromise(this.http, `/api/forummessage`, { forumThread: threadId, page_size: 1000 })
            .then((response: any) => (response && response.items) || []);
    }

    /** Post a comment or reply (data.parentForumMessageId), or edit one (data.id). */
    saveComment(data: any = {}) {
        return this.http.post(`/api/forummessage`, data)
            .toPromise()
    }

    deleteComment(id: number) {
        return doDeletePromise(this.http, `/api/forummessage/${id}`);
    }

    addAttachment(id: any, data: any = {}) {
        return this.http.post(`/api/filesystem/${id}`, data, { params: { section: 'wiki' } })
            .toPromise()
    }

    getMonitor(section, refId, user) {
        let params = {
            section: section,
            refId: refId,
            user: user
        };

        return doGetPromise(this.http, `/api/monitor`, params);
    }

    getMonitorList(section, refId) {
        let params = {
            section: section,
            refId: refId
        };

        return doGetPromise(this.http, `/api/monitor`, params);
    }
    
    deleteMonitor(section, refId, user) {
        let params = {
            sectionMonitoring: section,
            refId: refId,
            user: user
        };

        return doDeletePromise(this.http, `/api/monitor`, params);
    }

    getLock(id, params: any = {}) {
        return doGetPromise(this.http, `/api/wiki/${id}/lock`, params);
    }

    postLock(id: any, data: any) {
        return this.http.post(`/api/wiki/${id}/lock`, data)
            .toPromise()
    }

    /**
     * Set the public/private visibility of a user notebook page.
     * @param id Wiki page id
     * @param isPublic true to make the page publicly readable, false to make it private
     */
    postVisibility(id: any, isPublic: boolean) {
        return this.http.post(`/api/wiki/${id}/visibility`, { public: isPublic ? 1 : 0 })
            .toPromise()
    }
    
    deleteWiki(id: any) {
        return doDeletePromise(this.http, `/api/wiki/${id}`);
    }

    /**
     * Rename a wiki page, rewriting the wiki links on every page that refers to it.
     *
     * Pass `dryRun: true` to get the impact report without renaming anything — the same
     * call path produces both, so the preview shown in the confirmation modal cannot
     * disagree with what the rename actually does.
     *
     * @param id Wiki page id
     * @param pagename New page name
     * @param dryRun When true, report the impact and change nothing
     */
    renameWiki(id: any, pagename: string, dryRun: boolean = false): Promise<any> {
        const payload: any = { pagename };
        if (dryRun) { payload.dryRun = 1; }
        return this.http.post(`/api/wiki/${id}/rename`, payload)
            .toPromise();
    }

    setAttachment(wikiPageId: number, hash: any, data: any = {}, defer: boolean = false): Promise<any> {
        const payload = defer ? { ...data, defer: 1 } : data;
        return this.http.post(`/api/wiki/${wikiPageId}/attachment/${hash}`, payload)
            .toPromise();
    }

    getAttachments(wikiPageId: number, version?: number): Promise<any> {
        const params: any = {};
        if (version !== undefined) { params.version = version; }
        return doGetPromise(this.http, `/api/wiki/${wikiPageId}/attachments`, params);
    }

    deleteAttachment(wikiPageId: number, attachmentId: number): Promise<any> {
        return doDeletePromise(this.http, `/api/wiki/${wikiPageId}/attachment/${attachmentId}`);
    }

    stageAttachment(projectId: number, hash: string, data: any = {}): Promise<any> {
        return this.http.post(`/api/wiki/${projectId}/stagedattachment/${hash}`, data)
            .toPromise();
    }

    getAttachmentMeta(locationPath: string): Promise<any> {
        return doGetPromise(this.http, locationPath);
    }

    getOrphanCandidates(id: any) {
        return doGetPromise(this.http, `/api/wiki/${id}/orphans`);
    }

    /**
     * Download a wiki page as a PDF or Word document.
     *
     * The backend renders and converts synchronously and streams the file back
     *
     * @param id Wiki page id or page name
     * @param format 'pdf' or 'docx'
     * @param params Route scope — project/user, and optionally version
     */
    exportPage(id: any, format: 'pdf' | 'docx', params: any = {}): Promise<Blob> {
        return this.http
            .get(`/api/wiki/${id}/export`,
                 { params: { ...params, format }, responseType: 'blob' })
            .toPromise() as Promise<Blob>;
    }

    /**
     * Convert a chunk-uploaded document (Word, PowerPoint, Excel, PDF…) into Markdown
     * content for a page. The pictures it contained come back as files owned by the
     * page (not attachments) that the Markdown already links to.
     *
     * @param wikiPageId Wiki page id (the upload's refId, section `wikipage`)
     * @param hash Upload session hash from the chunk-upload endpoint
     * @param data `{fileName}` — the extension selects the converter
     * @returns Promise with {content, images, warnings}
     */
    importDocument(wikiPageId: number, hash: string, data: any = {}): Promise<any> {
        return this.http.post(`/api/wiki/${wikiPageId}/import/${hash}`, data)
            .toPromise();
    }

    /**
     * Discard an import that was not kept: its pictures are removed, except any the
     * page's saved content already references. Saving the page settles imports by itself.
     *
     * @param wikiPageId Wiki page id
     * @param importId The `importId` the import returned
     * @returns Promise with {removed}
     */
    discardImport(wikiPageId: number, importId: string): Promise<any> {
        return this.http.delete(`/api/wiki/${wikiPageId}/import/${importId}`)
            .toPromise();
    }

    /**
     * Convert a wiki page's HTML content to Markdown format.
     * Returns the converted markdown content along with quality metrics.
     * 
     * @param id Wiki page ID
     * @returns Promise with {markdown, originalHtml, quality, validation}
     */
    convertToMarkdown(id: number): Promise<any> {
        return this.http.post(`/api/wiki/${id}/convert`, {})
            .toPromise();
    }

    /**
     * Get wiki page content with format information.
     *
     * @param id Wiki page ID
     * @param params Additional parameters
     * @returns Promise with wiki content including content_type field
     */
    getContent(id: number, params: any = {}): Promise<any> {
        return doGetPromise(this.http, `/api/wiki/${id}/content`, params);
    }

    /**
     * Create a wiki page for an arbitrary section/refId (e.g. trackeritem notebooks).
     * Uses POST /api/wiki with no id, which the backend treats as a create.
     */
    createForSection(section: string, refId: number, pagename: string, content: string = ''): Promise<any> {
        return this.http.post('/api/wiki', {
            section,
            refId,
            pagename,
            content,
            content_type: 'text/markdown'
        }).toPromise();
    }
}
